import {
  languageOf,
  MACHINE_TRANSLATION_NOT_RECOGNISED_WARNING,
  MARKETS_WITH_PLAY,
  type AssetType,
  type InventoryCell,
  type InventoryListing,
  type InventoryScanResource,
  type ListingSnapshotAsset,
  type ListingSnapshotResource,
  type StoreListingResource,
  type Target,
} from '@studio/shared';

/** One country of a Store Listing, judged from its one or two Market Locales. */
export interface CountryLine {
  readonly market: string;
  readonly cells: readonly InventoryCell[];
  /** `gap` when any language it reads is a Translation Gap; `unread` while any of them was not read. */
  readonly state: 'covered' | 'gap' | 'unread';
}

export interface ListingSummary {
  readonly countries: readonly CountryLine[];
  readonly covered: number;
  readonly total: number;
  readonly gaps: readonly InventoryCell[];
  /** Translated text over graphics that are still the default language's. */
  readonly untranslatedGraphics: readonly InventoryCell[];
  readonly unread: number;
  readonly changed: number;
}

/** Where a Store Listing stands: what its one line in the Inventory leads with. */
export type ListingStanding = 'noUrl' | 'unread' | 'read';

/** One Store Listing as the Inventory lists it: judged once, when it is read from the Studio. */
export interface ListingLine {
  readonly listing: InventoryListing;
  readonly summary: ListingSummary;
  readonly standing: ListingStanding;
}

export function lineOf(listing: InventoryListing): ListingLine {
  const summary = summarize(listing);
  return { listing, summary, standing: standingOf(listing, summary) };
}

function standingOf(listing: InventoryListing, summary: ListingSummary): ListingStanding {
  const { storeListing } = listing;
  if (storeListing.url === null) return 'noUrl';
  return listing.cells.length > 0 && summary.unread < listing.cells.length ? 'read' : 'unread';
}

/** One thing Store Listings can share that the operator acts on; `all` narrows nothing. */
export const FACETS = ['all', 'gaps', 'graphics', 'unread', 'retired'] as const;
export type Facet = (typeof FACETS)[number];

export const FACET_LABEL: Record<Facet, string> = {
  all: 'Все',
  gaps: 'Translation Gaps',
  graphics: 'Untranslated Graphics',
  unread: 'Не прочитано',
  retired: 'Возможно удалён',
};

export function inFacet(facet: Facet, line: ListingLine): boolean {
  switch (facet) {
    case 'all':
      return true;
    case 'gaps':
      return line.summary.gaps.length > 0;
    case 'graphics':
      return line.summary.untranslatedGraphics.length > 0;
    case 'unread':
      return line.summary.unread > 0;
    case 'retired':
      return line.listing.possiblyRetired;
  }
}

export function facetCounts(lines: readonly ListingLine[]): Record<Facet, number> {
  const found: Record<Facet, number> = { all: 0, gaps: 0, graphics: 0, unread: 0, retired: 0 };
  for (const line of lines) for (const facet of FACETS) if (inFacet(facet, line)) found[facet] += 1;
  return found;
}

/** A Store Listing's countries by what they see: the three parts of its coverage meter. */
export interface Coverage {
  readonly covered: number;
  readonly gap: number;
  readonly unread: number;
}

export function coverageOf(summary: ListingSummary): Coverage {
  const gap = summary.countries.filter((country) => country.state === 'gap').length;
  return { covered: summary.covered, gap, unread: Math.max(0, summary.total - summary.covered - gap) };
}

export function summarize(listing: InventoryListing): ListingSummary {
  const byMarket = new Map<string, InventoryCell[]>();
  for (const cell of listing.cells) {
    const held = byMarket.get(cell.market) ?? [];
    held.push(cell);
    byMarket.set(cell.market, held);
  }
  const countries = [...byMarket.entries()].map(([market, cells]): CountryLine => {
    // Covered means every language the country reads was read and is not a gap; a
    // partial scan must not count a country it has only half seen.
    const state = cells.some((cell) => cell.state === 'gap') ? 'gap' : cells.some((cell) => cell.state === 'unread') ? 'unread' : 'covered';
    return { market, cells, state };
  });
  return {
    countries,
    covered: countries.filter((country) => country.state === 'covered').length,
    total: MARKETS_WITH_PLAY.length,
    gaps: listing.cells.filter((cell) => cell.state === 'gap'),
    untranslatedGraphics: listing.cells.filter((cell) => cellGraphicsOf(cell, listing.defaultLocale) === 'untranslated'),
    unread: listing.cells.filter((cell) => cell.state === 'unread').length,
    changed: listing.cells.filter((cell) => cell.changed).length,
  };
}

const regions = safeDisplayNames('region');
const languages = safeDisplayNames('language');

function safeDisplayNames(type: 'region' | 'language'): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames(['ru'], { type });
  } catch {
    return null;
  }
}

export function countryName(market: string): string {
  try {
    return regions?.of(market) ?? market;
  } catch {
    return market;
  }
}

/** Play's own tags, `iw` and `fil` included, as a reader would say them. */
export function languageName(locale: string): string {
  const tag = locale.replace(/^iw/, 'he');
  try {
    return languages?.of(tag) ?? locale;
  } catch {
    return locale;
  }
}

const pluralRules = safePluralRules();

function safePluralRules(): Intl.PluralRules | null {
  try {
    return new Intl.PluralRules('ru');
  } catch {
    return null;
  }
}

/** `2 страны`, `5 стран`: a number with its word in the form Russian counts it in. */
export function counted(count: number, one: string, few: string, many: string): string {
  const form = pluralRules?.select(count) ?? 'many';
  return `${count} ${form === 'one' ? one : form === 'few' ? few : many}`;
}

/** The scan of one Store Listing in the few words its line has room for. */
export function scanBrief(scan: InventoryScanResource): string {
  if (scan.notOnPlay) return 'Нет в Google Play';
  const when = new Date(scan.finishedAt ?? scan.startedAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return scan.state === 'stopped' ? `Остановлено ${when}` : when;
}

export function scanLine(scan: InventoryScanResource | null): string {
  if (scan === null) return 'Ещё не сканировали.';
  const when = new Date(scan.finishedAt ?? scan.startedAt).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' });
  if (scan.state === 'reading') {
    return scan.planned === 0
      ? 'Сканирование… старт'
      : `Сканирование… ${scan.read + scan.missed} из ${scan.planned} Market Locales`;
  }
  if (scan.notOnPlay) return `Нет в Google Play · проверено ${when}`;
  const counted = `${scan.read} из ${scan.planned} прочитано${scan.missed > 0 ? `, ${scan.missed} не прочитано` : ''}`;
  return scan.state === 'stopped' ? `Остановлено ${when} · ${counted}` : `Сканировано ${when} · ${counted}`;
}

/**
 * What the line of a Store Listing says about its scan, and the export with it: its own
 * scan going or done, an App scan reading it, a scan waiting its turn; null where the
 * line says nothing. An App scan that reads it is told on the App's line as it goes.
 */
export function listingScanOf(listing: InventoryListing, appReading: boolean): string | null {
  if (listing.latestScan?.state === 'reading') return scanLine(listing.latestScan);
  if (listing.scanPending) return appReading ? 'Сканирую…' : 'В очереди';
  return listing.latestScan === null ? null : scanBrief(listing.latestScan);
}

export const CELL_LABEL: Record<InventoryCell['state'], string> = {
  default: 'Язык по умолчанию',
  translated: 'Есть перевод',
  gap: 'Translation Gap',
  unread: 'Не прочитано',
};

/** What the line of a Store Listing says when there is no reading of it to draw. */
export const STANDING_LABEL: Record<Exclude<ListingStanding, 'read'>, string> = {
  noUrl: 'Без URL: прочитать нельзя',
  unread: 'Ещё не прочитано',
};

/**
 * What the graphics of one Market Locale are: never read, read but not judged, its own,
 * or the default language's - and those last ones are Untranslated Graphics only under
 * translated text. In the default language itself, and under a Translation Gap, the
 * default language's graphics go with the default language's text; nothing is missing
 * that the cell does not already say.
 */
export type CellGraphics = 'unread' | 'unknown' | 'own' | 'untranslated' | 'default';

/**
 * The one rule for the graphics of a cell: the flag on the cell, the count of the
 * Untranslated Graphics facet and the column of the export all ask it.
 */
export function cellGraphicsOf(cell: InventoryCell, defaultLocale: string | null): CellGraphics {
  if (cell.snapshotId === null) return 'unread';
  if (cell.graphicsLocalized === null) return 'unknown';
  if (cell.graphicsLocalized) return 'own';
  const otherLanguage = defaultLocale === null || languageOf(cell.locale) !== languageOf(defaultLocale);
  return cell.state === 'translated' && otherLanguage ? 'untranslated' : 'default';
}

export const GRAPHICS_LABEL: Record<CellGraphics, string> = {
  unread: 'не прочитано',
  unknown: 'неизвестно',
  own: 'своя графика',
  untranslated: 'графика не переведена',
  default: 'графика языка по умолчанию',
};

/** What one graphic of a reading is called, as the operator knows it. */
export const ASSET_LABEL: Record<ListingSnapshotAsset['role'], string> = {
  appIcon: 'Иконка',
  featureGraphic: 'Feature graphic',
  screenshot: 'Скриншот',
  previewVideo: 'Трейлер',
};

/** Gaps of one language: a language is translated once, for every country that reads it. */
export interface GapGroup {
  readonly locale: string;
  readonly cells: readonly InventoryCell[];
  readonly machineTranslated: boolean;
}

export function gapsByLanguage(gaps: readonly InventoryCell[]): readonly GapGroup[] {
  const byLocale = new Map<string, InventoryCell[]>();
  for (const cell of gaps) byLocale.set(cell.locale, [...(byLocale.get(cell.locale) ?? []), cell]);
  return [...byLocale.entries()]
    .map(([locale, cells]) => ({ locale, cells, machineTranslated: cells.some((cell) => cell.machineTranslated) }))
    .sort((left, right) => right.cells.length - left.cells.length);
}

/** The Market Locale the operator has open, kept in the address so a return finds it. */
export interface OpenCell {
  readonly storeListingId: string;
  readonly market: string;
  readonly locale: string;
}

/**
 * The open cell in the address, or nothing when no cell is open.
 *
 * The address is what a return reads: the operator leaves for an Experiment and comes
 * back to the same reading, not to a closed list.
 */
export function cellInAddress(search: string): OpenCell | null {
  return targetInAddress(search, 'cell');
}

/** The open cell as the address carries it. */
export function cellAddress(cell: OpenCell): string {
  return `${cell.storeListingId}|${cell.market}|${cell.locale}`;
}

/** The comparison the operator has open, kept in the address like the open cell. */
export type ComparisonTarget = OpenCell;

/**
 * The comparison in the address, or nothing when none is open.
 *
 * It is one cell of one Store Listing: the comparison is about two readings of the same
 * Market Locale and never about two of different ones.
 */
export function comparisonInAddress(search: string): ComparisonTarget | null {
  return targetInAddress(search, 'compare');
}

/** The comparison as the address carries it. */
export function comparisonAddress(target: ComparisonTarget): string {
  return `${target.storeListingId}|${target.market}|${target.locale}`;
}

function targetInAddress(search: string, name: 'cell' | 'compare'): OpenCell | null {
  const raw = new URLSearchParams(search).get(name);
  if (raw === null) return null;
  const [storeListingId, market, locale] = raw.split('|');
  if (storeListingId === undefined || market === undefined || locale === undefined) return null;
  if (storeListingId === '' || market === '' || locale === '') return null;
  return { storeListingId, market, locale };
}

/** How a Store Listing is called in the Inventory, on the screen and in the export. */
export function storeListingLabel(storeListing: StoreListingResource): string {
  return storeListing.kind === 'main' ? 'Main Store Listing' : storeListing.name;
}

/**
 * One image is one image: the size Play puts at the end of the address is not another
 * picture. The rule the readings themselves are compared by - the same one the server
 * uses when it says that a listing's graphics changed.
 */
export function imageIdentity(asset: ListingSnapshotAsset): string {
  const url = asset.sourceUrl;
  if (url === null) return `artifact ${asset.artifactId ?? ''}`;
  if (!url.startsWith('https://play-lh.googleusercontent.com/')) return url;
  return url.replace(/=[^/]*$/, '');
}

/**
 * The graphics a reading shows, in the order it holds them: the icon, the feature graphic,
 * the screenshots and the trailer. Every one of them is what the Studio marks a cell
 * changed by, so every one of them is what the comparison shows.
 */
export function graphicsOf(snapshot: ListingSnapshotResource): readonly ListingSnapshotAsset[] {
  return snapshot.assets.slice().sort((left, right) => left.position - right.position);
}

/**
 * The roles a reading could not observe at all. A page read from its markup never shows
 * the feature graphic - only Play's own data does - so its absence there is not a removal.
 * The sentence the scan wrote for such a reading is what tells it, as for the machine
 * translation.
 */
function unobservedRolesOf(snapshot: ListingSnapshotResource): readonly ListingSnapshotAsset['role'][] {
  const fromMarkup = snapshot.warnings.some((warning) => warning === MACHINE_TRANSLATION_NOT_RECOGNISED_WARNING);
  if (!fromMarkup) return [];
  return snapshot.assets.some((asset) => asset.role === 'featureGraphic') ? [] : ['featureGraphic'];
}

/** One text of a reading, and what became of it between two readings. */
export interface ComparedText {
  readonly label: string;
  /**
   * `changed` and `unchanged` only when both readings observed the text. When one of
   * them did not, nothing is known of a change, and it is never called one.
   */
  readonly change: 'changed' | 'unchanged' | 'unobserved';
  /** Null when that reading did not observe this part at all. */
  readonly before: string | null;
  readonly after: string | null;
}

/** One occurrence of a graphic in a reading, and what became of it in the other reading. */
export interface ComparedGraphic {
  /** Its place in the order the reading shows, counted from one. */
  readonly position: number;
  readonly role: ListingSnapshotAsset['role'];
  readonly url: string | null;
  /**
   * `same` - in both readings, in the order the two share; `moved` - in both, out of that
   * order; `added`, `removed` - in one of them only; `unobserved` - in this one only, and
   * the other reading could not have seen it.
   */
  readonly mark: 'same' | 'added' | 'removed' | 'moved' | 'unobserved';
  /** The place it holds in the other reading, when it is in both. */
  readonly other: number | null;
}

export interface GraphicsChange {
  readonly before: readonly ComparedGraphic[];
  readonly after: readonly ComparedGraphic[];
  /** The roles each reading could not observe: nothing in them is added or removed. */
  readonly unobserved: {
    readonly before: readonly ListingSnapshotAsset['role'][];
    readonly after: readonly ListingSnapshotAsset['role'][];
  };
}

/** Two readings of one Market Locale, and what is different between them. */
export interface ReadingComparison {
  readonly texts: readonly ComparedText[];
  readonly graphics: GraphicsChange;
}

const COMPARED_TEXTS = [
  { key: 'title', label: 'Заголовок' },
  { key: 'shortDescription', label: 'Краткое описание' },
  { key: 'fullDescription', label: 'Полное описание' },
] as const;

/**
 * When a reading was read, as the screen says it: the day the storefront was read, and for
 * a reading the operator corrected, the day of the correction beside it. A correction is a
 * snapshot of its own, written when it was made, so its own `capturedAt` is not that day.
 * When that day could not be found out (`readAt` is null), the screen says so in words.
 */
export function readingWhen(readAt: string | null, reviewedAt: string | null): string {
  const day = (at: string): string => new Date(at).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' });
  const read = readAt === null ? 'дату чтения витрины установить не удалось' : day(readAt);
  return reviewedAt === null ? read : `${read} · исправлено оператором ${day(reviewedAt)}`;
}

/** What a reading observed of Play's own machine translation; null when it could not tell. */
export function machineTranslationOf(snapshot: ListingSnapshotResource): boolean | null {
  if (snapshot.machineTranslated) return true;
  return snapshot.warnings.some((warning) => warning === MACHINE_TRANSLATION_NOT_RECOGNISED_WARNING) ? null : false;
}

/** One picture in one role, whatever its size: the key two readings' graphics are paired by. */
function graphicKey(asset: ListingSnapshotAsset): string {
  return `${asset.role} ${imageIdentity(asset)}`;
}

/**
 * The occurrences two readings show in the same order: the longest run of them that both
 * show one after another, each occurrence paired at most once, by index before → index now.
 * A picture taken away or put in does not shift the ones around it out of this order.
 */
function keptInOrder(before: readonly string[], after: readonly string[]): ReadonlyMap<number, number> {
  const width = after.length + 1;
  // longest[i * width + j]: how many of before[i..] and after[j..] pair up in order.
  const longest = new Array<number>((before.length + 1) * width).fill(0);
  const at = (i: number, j: number): number => longest[i * width + j] ?? 0;
  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      longest[i * width + j] = before[i] === after[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const kept = new Map<number, number>();
  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (before[i] === after[j]) {
      kept.set(i, j);
      i += 1;
      j += 1;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      i += 1;
    } else {
      j += 1;
    }
  }
  return kept;
}

/** One occurrence of the reading before paired with one of the reading now. */
interface PairedGraphic {
  readonly before: number;
  readonly after: number;
  readonly mark: 'same' | 'moved';
}

/**
 * Every occurrence paired with at most one occurrence of the same picture in the other
 * reading. A picture Play shows twice is two occurrences, never one.
 */
function pairedGraphics(before: readonly string[], after: readonly string[]): readonly PairedGraphic[] {
  const kept = keptInOrder(before, after);
  const keptNow = new Set(kept.values());
  // Out of that order: each occurrence left over before goes with the first occurrence
  // of the same picture left over now - that one really changed its place.
  const left = after.flatMap((key, index) => (keptNow.has(index) ? [] : [{ key, index }]));
  const moved = before.flatMap((key, index): PairedGraphic[] => {
    if (kept.has(index)) return [];
    const found = left.findIndex((one) => one.key === key);
    const [match] = found === -1 ? [] : left.splice(found, 1);
    return match === undefined ? [] : [{ before: index, after: match.index, mark: 'moved' }];
  });
  return [...[...kept].map(([was, now]): PairedGraphic => ({ before: was, after: now, mark: 'same' })), ...moved];
}

/** What two readings of the same Market Locale say about each other. */
export function compareReadings(before: ListingSnapshotResource, after: ListingSnapshotResource): ReadingComparison {
  const texts = COMPARED_TEXTS.map(({ key, label }): ComparedText => {
    const was = before[key];
    const now = after[key];
    const change = was === null || now === null ? 'unobserved' : was === now ? 'unchanged' : 'changed';
    return { label, change, before: was, after: now };
  });
  const beforeList = graphicsOf(before);
  const afterList = graphicsOf(after);
  const pairs = pairedGraphics(beforeList.map(graphicKey), afterList.map(graphicKey));
  const fromBefore = new Map(pairs.map((pair) => [pair.before, pair]));
  const fromAfter = new Map(pairs.map((pair) => [pair.after, pair]));
  const unobservedBefore = unobservedRolesOf(before);
  const unobservedAfter = unobservedRolesOf(after);
  return {
    texts,
    graphics: {
      before: beforeList.map((asset, index): ComparedGraphic => {
        const pair = fromBefore.get(index);
        return {
          position: index + 1,
          role: asset.role,
          url: asset.sourceUrl,
          mark: pair?.mark ?? (unobservedAfter.includes(asset.role) ? 'unobserved' : 'removed'),
          other: pair === undefined ? null : pair.after + 1,
        };
      }),
      after: afterList.map((asset, index): ComparedGraphic => {
        const pair = fromAfter.get(index);
        return {
          position: index + 1,
          role: asset.role,
          url: asset.sourceUrl,
          mark: pair?.mark ?? (unobservedBefore.includes(asset.role) ? 'unobserved' : 'added'),
          other: pair === undefined ? null : pair.before + 1,
        };
      }),
      unobserved: { before: unobservedBefore, after: unobservedAfter },
    },
  };
}

/** The Target a cell means: the market and the locale it was read in. */
export function targetOf(cell: InventoryCell): Target {
  return { kind: 'market', market: cell.market, locale: cell.locale };
}

/**
 * The Control a Target of a Store Listing gets: the reading the Studio compares an
 * Experiment with, found by the server's own rule (`listingSnapshotFor`).
 *
 * A Translation Gap is never one. A translation is the listing's for a language, not for
 * a country, so the Target's own Market Locale is asked first, then its country in the
 * same language, then its locale and its language anywhere; failing those, the default
 * language. A reading in any other language is no Control for this Target. Without one
 * an Experiment would have nothing to be better than, and the operator is told so.
 */
export function controlOf(listing: InventoryListing, target: Pick<InventoryCell, 'market' | 'locale'>): InventoryCell | null {
  const read = listing.cells.filter((cell) => cell.state !== 'gap' && cell.snapshotId !== null);
  const newestFirst = [...read].sort((left, right) => (right.capturedAt ?? '').localeCompare(left.capturedAt ?? ''));
  const language = languageOf(target.locale);
  const ownLanguage = [
    ...read.filter((cell) => cell.market === target.market && cell.locale === target.locale),
    ...newestFirst.filter((cell) => cell.market === target.market && languageOf(cell.locale) === language),
    ...newestFirst.filter((cell) => cell.locale === target.locale),
    ...newestFirst.filter((cell) => languageOf(cell.locale) === language),
  ];
  const chosen = ownLanguage[0];
  if (chosen !== undefined) return chosen;
  // The default language is known only once the readings tell it.
  if (listing.defaultLocale === null) return null;
  const standard = languageOf(listing.defaultLocale);
  return newestFirst.find((cell) => languageOf(cell.locale) === standard) ?? null;
}

/** The graphics of a reading each Asset Type is compared with: the server's `SNAPSHOT_ROLE`. */
const CONTROL_ROLE: Record<AssetType, ListingSnapshotAsset['role']> = {
  appIcon: 'appIcon',
  featureGraphic: 'featureGraphic',
  screenshotSet: 'screenshot',
  previewVideo: 'previewVideo',
};

/** What a reading holds of one Asset Type, and which of it a Control can be made of. */
export interface ControlGraphics {
  /** Every graphic of that type the reading saw on the page, fetched or not. */
  readonly shown: readonly ListingSnapshotAsset[];
  /** The ones the Studio holds a file of. A Control is these, and there is none without them. */
  readonly kept: readonly ListingSnapshotAsset[];
}

/**
 * The Control an Experiment of one Asset Type gets out of the reading `controlOf` chose,
 * by the server's own rule (`listingPreview`): the graphics of that type the Studio
 * holds a file of. A graphic seen on the page whose bytes never came is nothing to
 * compare with, and neither is a trailer: the Studio keeps only its address on YouTube,
 * which `Mark Live` freezes later, but no Experiment starts with it.
 */
export function controlGraphicsOf(snapshot: ListingSnapshotResource, assetType: AssetType): ControlGraphics {
  const shown = snapshot.assets.filter((asset) => asset.role === CONTROL_ROLE[assetType]);
  return { shown, kept: shown.filter((asset) => asset.artifactId !== null) };
}

/**
 * The trailer address `Mark Live` freezes as the Control of a Preview video, by the
 * server's own rule (`freezeControl`): the address of the reading's first trailer by
 * position. A trailer row without an address freezes no address, and so null.
 */
export function frozenTrailerOf(snapshot: ListingSnapshotResource): string | null {
  const [first] = graphicsOf(snapshot).filter((asset) => asset.role === 'previewVideo');
  return first?.sourceUrl ?? null;
}
